import { InputPhoneLib, PlaceAutoComplete } from "components";
import { Paper, Button, Grid, Typography, Box, Divider } from "components/mui";
import {
  CompanySelector,
  ContactInput,
} from "containers/pages/contacts/components";
import {
  ImgUploader,
} from "containers/pages/contacts/components";
import {
  InputNote,
  FullAddressField,
  SelectLanguages,
} from "containers/pages/contacts/components";
import * as TY from "containers/pages/users/constants";
import useContactInfoForm from "./useContactInfoForm";
import {
  funcType,
  stringType,
  userType,
  validType,
} from "containers/pages/users/types";
import React from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

/**
 * @component ContactInfoForm
 * @date Jun 15, 2023
 * @param {Object} props
 * @param {object} props.user
 * @param {object} props.valid
 * @param {string} props.addressType
 * @param {function} props.dispatch
 * @param {function} props.handleForm
 * @param {function} props.onChangeValid
 * @param {function} props.onChangeTogBtn
 * @returns {JSX.Element}
 */
const ContactInfoForm = (props) => {
  const state = useContactInfoForm(props.dispatch, props.addressType);

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        padding: 3,
        margin: 2,
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        data-tour="user-view-toggle"
        value={props.complexity}
        onChange={props.onComplexityChange}
        color="primary"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="simple">Simplified</ToggleButton>
        <ToggleButton value="full">Full</ToggleButton>
      </ToggleButtonGroup>
      {props.complexity === "simple" ? (
        // SIMPLE UI
        <>
        <Box mb={4}>
            <Typography variant="h6" color="text.primary" mb={1}>
              User Details
            </Typography>
            <Divider />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ImgUploader contact={props.user} dispatch={props.dispatch} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <ContactInput
                  name={TY.F_NAME}
                  label="First Name"
                  type="text"
                  required
                  value={props.user[TY.F_NAME]}
                  valid={props.valid[TY.F_NAME]}
                  onChange={props.handleForm}
                  fullWidth
                />
                <ContactInput
                  name={TY.L_NAME}
                  label="Last Name"
                  type="text"
                  required
                  value={props.user[TY.L_NAME]}
                  valid={props.valid[TY.L_NAME]}
                  onChange={props.handleForm}
                  fullWidth
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <ContactInput
                  name={TY.EMAIL_WORK}
                  label="Email"
                  type="email"
                  value={props.user[TY.EMAIL_WORK]}
                  valid={props.valid[TY.EMAIL_WORK]}
                  onChange={props.handleForm}
                  fullWidth
                />
                <InputPhoneLib
                  name={TY.PHONE_WORK}
                  label="Work"
                  value={props.user[TY.PHONE_WORK]}
                  onChange={props.handleForm}
                  onChangeValid={props.onChangeValid}
                  fullWidth
                />
                <CompanySelector
                  value={props.user[TY.COMPANIES]}
                  options={state.companies}
                  valid={props.valid[TY.COMPANIES]}
                  handleForm={props.handleForm}
                />
              </Box>
            </Grid>
          </Grid>
        </>
      ) : (
        // FULL UI
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6" color="text.primary">
                User Details
              </Typography>
              <Divider />
              <ImgUploader contact={props.user} dispatch={props.dispatch} />
              <div data-tour="user-first-name">
                <ContactInput
                  name={TY.F_NAME}
                  label="First Name"
                  type="text"
                  required
                  value={props.user[TY.F_NAME]}
                  valid={props.valid[TY.F_NAME]}
                  onChange={props.handleForm}
                  fullWidth
                />
              </div>
              <div data-tour="user-last-name">
                <ContactInput
                  name={TY.L_NAME}
                  label="Last Name"
                  type="text"
                  required
                  value={props.user[TY.L_NAME]}
                  valid={props.valid[TY.L_NAME]}
                  onChange={props.handleForm}
                  fullWidth
                />
              </div>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6" color="text.primary">
                Email
              </Typography>
              <Divider />
              <ContactInput
                name={TY.EMAIL_WORK}
                label="Work"
                type="email"
                value={props.user[TY.EMAIL_WORK]}
                valid={props.valid[TY.EMAIL_WORK]}
                onChange={props.handleForm}
                fullWidth
              />
              <ContactInput
                name={TY.EMAIL_PERSONAL}
                label="Personal"
                type="email"
                value={props.user[TY.EMAIL_PERSONAL]}
                valid={props.valid[TY.EMAIL_PERSONAL]}
                onChange={props.handleForm}
                fullWidth
              />

              <div data-tour="user-companies">
                <CompanySelector
                  value={props.user[TY.COMPANIES]}
                  options={state.companies}
                  valid={props.valid[TY.COMPANIES]}
                  handleForm={props.handleForm}
                />
              </div>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6" color="text.primary">
                Phone
              </Typography>
              <Divider />
              <InputPhoneLib
                name={TY.PHONE_WORK}
                label="Work"
                value={props.user[TY.PHONE_WORK]}
                onChange={props.handleForm}
                onChangeValid={props.onChangeValid}
                fullWidth
              />
              <InputPhoneLib
                name={TY.PHONE_MOBILE}
                label="Mobile"
                value={props.user[TY.PHONE_MOBILE]}
                onChange={props.handleForm}
                onChangeValid={props.onChangeValid}
                fullWidth
              />
              <SelectLanguages
                value={props.user[TY.LANGUAGES]}
                dispatch={props.dispatch}
                options={state.languages}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6" color="text.primary">
                Address
              </Typography>
              <Divider />
              {props.user[props.addressType].fullAddress.length === 0 ? (
                <PlaceAutoComplete handlePlace={state.handlePlace} />
              ) : (
                <FullAddressField
                  type={props.addressType}
                  value={props.user[props.addressType].fullAddress}
                  contact={props.user}
                  dispatch={props.dispatch}
                />
              )}
              <ContactInput
                name={TY.UNIT}
                label="# Unit"
                type="text"
                value={props.user[props.addressType].unit}
                onChange={props.handleForm}
                fullWidth
              />
              <InputNote contact={props.user} handleForm={props.handleForm} />
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

ContactInfoForm.propsType = {
  user: userType,
  valid: validType,
  addressType: stringType,
  dispatch: funcType,
  handleForm: funcType,
  onChangeValid: funcType,
  complexity: stringType,
  onComplexityChange: funcType,
};

export default ContactInfoForm;
